import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, signInWithPopup, signInWithPhoneNumber, RecaptchaVerifier, PhoneAuthProvider, signInWithCredential, signInWithRedirect, getRedirectResult } from '@firebase/auth';
import { auth, provider } from '../firebase';
import BrandLogo from '../components/BrandLogo';
import { useBrandingConfig } from '../utils/siteConfig';
import { takeAccountNotice } from '../utils/accountStatus';

export default function Login() {
  const navigate = useNavigate();
  const { config } = useBrandingConfig('published');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [showPhoneLogin, setShowPhoneLogin] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [error, setError] = useState<string | null>(() => takeAccountNotice());
  const [loading, setLoading] = useState(false);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState<any | null>(null);

  useEffect(() => {
    if (showPhoneLogin && !recaptchaVerifier) {
      const verifier = new RecaptchaVerifier('recaptcha-container', {
        size: 'invisible',
      }, auth);
      setRecaptchaVerifier(verifier);
    }
  }, [showPhoneLogin, recaptchaVerifier]);

  // Complete redirect-based Google sign-in if a redirect flow just returned
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const result = await getRedirectResult(auth);
        if (!active) return;
        if (result && result.user) {
          navigate('/home');
        }
      } catch (e) {
        // ignore; regular login UI remains available
      }
    })();
    return () => { active = false; };
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/home');
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError(null);
    setLoading(true);
    try {
      await signInWithPopup(auth, provider);
      navigate('/home');
    } catch (err: any) {
      // Fallback to redirect in browsers/webviews that block popups (e.g., VS Code Simple Browser)
      const code = err?.code || '';
      const shouldRedirect = (
        code === 'auth/popup-blocked' ||
        code === 'auth/popup-closed-by-user' ||
        code === 'auth/cancelled-popup-request' ||
        code === 'auth/operation-not-supported-in-this-environment'
      );
      if (shouldRedirect) {
        try {
          await signInWithRedirect(auth, provider);
          return; // navigation continues after redirect completes
        } catch (redirErr: any) {
          setError(redirErr?.message || 'Google sign-in (redirect) failed');
        }
      } else {
        setError(err.message || 'Google sign-in failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recaptchaVerifier) {
      setError('Recaptcha not initialized');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
      setVerificationId(confirmationResult.verificationId);
      setShowVerification(true);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
      await signInWithCredential(auth, credential);
      navigate('/home');
    } catch (err: any) {
      setError(err.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.cardWrap}>
        <div style={styles.leftSection}>
          {config.logoUrl ? (
            <picture>
              <source srcSet={`${config.logoUrl}?w=160&fmt=webp 1x, ${config.logoUrl}?w=320&fmt=webp 2x`} type="image/webp" />
              <source srcSet={`${config.logoUrl}?w=160 1x, ${config.logoUrl}?w=320 2x`} />
              <img src={config.logoUrl} alt={config.brandName} style={{ height: 48, objectFit: 'contain', marginBottom: 8 }} loading="eager" fetchPriority="high" decoding="async" />
            </picture>
          ) : (
            <h1 style={styles.brand}><BrandLogo text={config.brandName || 'Belivah'} size={46} color="#fff" iconColor="#0E8F94" /></h1>
          )}
          <h2 style={styles.tagline}>{config.tagline}</h2>
          <p style={styles.welcome}>{config.loginWelcome}</p>
          <img src={config.authImageUrl || '/belivah-image.png'} alt="Belivah community" style={styles.image} loading="lazy" decoding="async" />
        </div>
        <div style={styles.formSection}>
          <h2 style={styles.formTitle}>Sign in to your account</h2>
          {error && <div style={styles.error}>{error}</div>}
          
          {!showPhoneLogin ? (
            <>
              <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Email</label>
                  <input 
                    value={email} 
                    onChange={e => setEmail(e.target.value)} 
                    type="email" 
                    required
                    style={styles.input}
                    placeholder="Email address"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Password</label>
                  <input 
                    value={password} 
                    onChange={e => setPassword(e.target.value)} 
                    type="password" 
                    required
                    style={styles.input}
                    placeholder="Password"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  style={styles.submitBtn}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 18px rgba(23,162,169,0.3)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(23,162,169,0.10)'; }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                  onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>

              <div style={styles.divider}>
                <span style={styles.dividerText}>OR</span>
              </div>

              <button
                onClick={handleGoogle}
                disabled={loading}
                style={styles.googleBtn}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#17A2A9'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 14px rgba(23,162,169,0.2)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E0E0E0'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
              >
                <span style={styles.googleIcon}>G</span> Continue with Google
              </button>


            </>
          ) : !showVerification ? (
            <>
              <form onSubmit={handleSendCode} style={styles.form}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Phone Number</label>
                  <input 
                    value={phoneNumber} 
                    onChange={e => setPhoneNumber(e.target.value)} 
                    type="tel" 
                    required
                    style={styles.input}
                    placeholder="+1234567890 (include country code)"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  style={styles.submitBtn}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 18px rgba(23,162,169,0.3)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(23,162,169,0.10)'; }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                  onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                >
                  {loading ? 'Sending Code...' : 'Send Verification Code'}
                </button>
              </form>
              <button
                onClick={() => setShowPhoneLogin(false)}
                style={styles.backBtn}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#0B6B6F'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#17A2A9'; }}
              >
                ← Back to email login
              </button>
              <div id="recaptcha-container"></div>
            </>
          ) : (
            <>
              <form onSubmit={handleVerifyCode} style={styles.form}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Verification Code</label>
                  <input 
                    value={verificationCode} 
                    onChange={e => setVerificationCode(e.target.value)} 
                    type="text" 
                    required
                    style={styles.input}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  style={styles.submitBtn}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 18px rgba(23,162,169,0.3)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(23,162,169,0.10)'; }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
                  onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                >
                  {loading ? 'Verifying...' : 'Verify & Sign In'}
                </button>
              </form>
              <button
                onClick={() => { setShowVerification(false); setVerificationCode(''); }}
                style={styles.backBtn}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#0B6B6F'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#17A2A9'; }}
              >
                ← Resend code
              </button>
            </>
          )}

          <div style={styles.signupLink}>
            Don't have an account? <a href="/signup" style={styles.link}>Sign up</a>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: '100vh',
    background: '#F5EDE1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Inter, Arial, sans-serif',
  },
  cardWrap: {
    display: 'flex',
    background: '#fff',
    borderRadius: '24px',
    boxShadow: '0 4px 24px rgba(11,107,111,0.10)',
    overflow: 'hidden',
    maxWidth: '900px',
    width: '100%',
    minHeight: '520px',
    flexWrap: 'wrap',
  },
  leftSection: {
    flex: 1,
    background: '#0B6B6F',
    color: '#fff',
    padding: '48px 32px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    minWidth: '320px',
  },
  brand: {
    fontSize: '2.5rem',
    fontWeight: 800,
    marginBottom: '8px',
    letterSpacing: '2px',
  },
  tagline: {
    fontSize: '1.2rem',
    fontWeight: 500,
    color: '#66EAD1',
    marginBottom: '18px',
    letterSpacing: '1px',
  },
  welcome: {
    fontSize: '1.1rem',
    marginBottom: '24px',
    color: '#F5EDE1',
    fontWeight: 400,
  },
  image: {
    width: '100%',
    maxWidth: '350px',
    borderRadius: '16px',
    background: '#fff',
    objectFit: 'contain',
    boxShadow: '0 2px 8px rgba(102,234,209,0.10)',
    marginTop: 'auto',
  },
  formSection: {
    flex: 1,
    padding: '48px 32px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: '320px',
  },
  formTitle: {
    fontSize: '2rem',
    color: '#0B6B6F',
    fontWeight: 700,
    marginBottom: '24px',
    textAlign: 'left',
  },
  error: {
    color: '#d32f2f',
    marginBottom: '16px',
    fontWeight: 500,
    padding: '12px',
    background: '#ffebee',
    borderRadius: '8px',
  },
  form: {
    width: '100%',
  },
  inputGroup: {
    marginBottom: '18px',
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: 500,
    color: '#072B2C',
    fontSize: '1rem',
  },
  input: {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '2px solid #17A2A9',
    fontSize: '1rem',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'box-shadow 0.2s, border-color 0.2s',
  },
  submitBtn: {
    width: '100%',
    padding: '14px',
    background: '#17A2A9',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1.1rem',
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: '8px',
    boxShadow: '0 2px 8px rgba(23,162,169,0.10)',
    transition: 'background 0.2s, box-shadow 0.2s, transform 0.2s',
  },
  divider: {
    position: 'relative',
    textAlign: 'center',
    margin: '24px 0',
  },
  dividerText: {
    background: '#fff',
    padding: '0 12px',
    color: '#5B6E6E',
    fontSize: '0.9rem',
    fontWeight: 500,
    position: 'relative',
    zIndex: 1,
  },
  googleBtn: {
    width: '100%',
    padding: '14px',
    background: '#fff',
    color: '#072B2C',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
  },
  googleIcon: {
    fontSize: '1.2rem',
    fontWeight: 700,
    color: '#17A2A9',
  },
  phoneBtn: {
    width: '100%',
    padding: '14px',
    background: '#fff',
    color: '#072B2C',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    marginTop: '12px',
    transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
  },
  phoneIcon: {
    fontSize: '1.2rem',
  },
  backBtn: {
    width: '100%',
    padding: '12px',
    background: 'transparent',
    color: '#17A2A9',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '16px',
    textAlign: 'center',
    transition: 'color 0.2s',
  },
  signupLink: {
    marginTop: '24px',
    textAlign: 'center',
    fontSize: '1rem',
    color: '#072B2C',
  },
  link: {
    color: '#17A2A9',
    textDecoration: 'none',
    fontWeight: 600,
    marginLeft: '4px',
  },
};
